import SiteCoreLinks from "@/components/SiteCoreLinks";
// EN mirror of /visa-comparison — side-by-side Thai visa comparison
import { createFileRoute, Link } from "@tanstack/react-router";
import { Scale } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { BRAND_URL } from "@/lib/brand";

const TITLE = "Thai Visa Comparison — LTR vs DTV vs Non-B vs Retirement 2026 | NYC";
const DESC = "Side-by-side comparison of Thailand's long-stay visa tracks: LTR, DTV, Non-B, retirement O, marriage O. Ask us anything — real experts reply within minutes.";

type Row = {
  attr: string;
  ltr: string; dtv: string; nonB: string; retirement: string; marriage: string;
};
const ROWS: Row[] = [
  { attr: "Duration", ltr: "10 years (5+5)", dtv: "5 years multi-entry", nonB: "1 year + annual extension", retirement: "1 year + annual", marriage: "1 year + annual" },
  { attr: "Per-stay limit", ltr: "Unlimited", dtv: "180 days / entry", nonB: "Unlimited (with 90-day report)", retirement: "Unlimited (90-day report)", marriage: "Unlimited (90-day report)" },
  { attr: "Income floor", ltr: "USD 80k / yr", dtv: "None (portfolio proof)", nonB: "THB 50k / month via sponsor", retirement: "THB 65k/mo OR THB 800k bank", marriage: "THB 40k/mo OR THB 400k bank" },
  { attr: "Work rights", ltr: "Yes (LTR work permit included)", dtv: "Remote work for foreign clients only", nonB: "Yes (via WP.3 employer)", retirement: "No", marriage: "Yes (with separate WP)" },
  { attr: "Age requirement", ltr: "None (each track differs)", dtv: "20+", nonB: "None", retirement: "50+", marriage: "None (must be married)" },
  { attr: "Family included", ltr: "Spouse + up to 4 children under 20", dtv: "Spouse + dependents", nonB: "Spouse + child (Non-O dependent)", retirement: "Spouse (Non-O dependent)", marriage: "N/A (spouse is Thai)" },
  { attr: "Tax status", ltr: "Foreign-source income exempt (BOI rule)", dtv: "Standard PIT if 180+ days", nonB: "Thai resident after 180 days", retirement: "Thai resident after 180 days", marriage: "Thai resident after 180 days" },
  { attr: "Processing time", ltr: "45–60 days (BOI)", dtv: "15–30 days", nonB: "9–14 business days", retirement: "10–15 days", marriage: "10–20 days" },
  { attr: "Our fee (service only)", ltr: "THB 35k+", dtv: "THB 18k", nonB: "THB 22k", retirement: "THB 15k", marriage: "THB 15k" },
];

export const Route = createFileRoute("/en/visa-comparison")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/en/visa-comparison", "en")}, { name: "description", content: ctaDesc(DESC, "/en/visa-comparison", "en")},
      { property: "og:title", content: ctaTitle(TITLE, "/en/visa-comparison", "en")}, { property: "og:description", content: ctaDesc(DESC, "/en/visa-comparison", "en")},
      { property: "og:url", content: `${BRAND_URL}/en/visa-comparison` }, { property: "og:locale", content: "en_US" },
    ],
    links: [
      { rel: "canonical", href: `${BRAND_URL}/en/visa-comparison` },
      ...hreflangLinks("/visa-comparison", "/en/visa-comparison"),
    ],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org", "@type": "Article",
        headline: TITLE, url: `${BRAND_URL}/en/visa-comparison`, inLanguage: "en",
        author: { "@type": "Organization", name: "NYC Language Institute" },
        datePublished: "2026-07-16",
      }),
    }],
  }),
  component: EnVisaCompare,
});

function EnVisaCompare() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep px-4 py-14 text-white">
        <div className="mx-auto max-w-6xl">
          <Link to="/en" className="text-xs text-amber-300 hover:underline">← EN home</Link>
          <div className="mt-3 flex items-center gap-3">
            <Scale className="h-8 w-8 text-amber-300" aria-hidden />
            <h1 className="text-3xl font-extrabold sm:text-4xl">Thai visa comparison</h1>
          </div>
          <p className="mt-3 max-w-3xl text-white/80">
            LTR vs DTV vs Non-B vs Retirement vs Marriage — nine attributes across five visa tracks.
            Filed against Immigration Order 327/2557 + BOI eLTR portal rules current for 2026.
          </p>
        </div>
      </section>
      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl overflow-x-auto">
          <table className="w-full min-w-[900px] border-collapse text-sm">
            <thead>
              <tr className="bg-slate-100 text-navy-deep">
                <th className="border-b border-slate-300 p-3 text-left">Attribute</th>
                <th className="border-b border-slate-300 p-3 text-left">LTR</th>
                <th className="border-b border-slate-300 p-3 text-left">DTV</th>
                <th className="border-b border-slate-300 p-3 text-left">Non-B</th>
                <th className="border-b border-slate-300 p-3 text-left">Retirement</th>
                <th className="border-b border-slate-300 p-3 text-left">Marriage</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.attr} className="odd:bg-white even:bg-slate-50">
                  <th className="border-b border-slate-200 p-3 text-left font-semibold text-navy-deep">{r.attr}</th>
                  <td className="border-b border-slate-200 p-3 text-slate-700">{r.ltr}</td>
                  <td className="border-b border-slate-200 p-3 text-slate-700">{r.dtv}</td>
                  <td className="border-b border-slate-200 p-3 text-slate-700">{r.nonB}</td>
                  <td className="border-b border-slate-200 p-3 text-slate-700">{r.retirement}</td>
                  <td className="border-b border-slate-200 p-3 text-slate-700">{r.marriage}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-6 text-xs text-slate-500">
            Sourced from BOI eLTR portal, Immigration Bureau Order 327/2557, and DTV cabinet resolution 24 May 2024. Individual cases vary — request a specific quote via LINE.
          </p>
          <p className="mt-3 text-xs text-slate-500">Thai version: <Link to="/visa-comparison" className="text-amber-700 underline">/visa-comparison</Link></p>
        </div>
      </section>
          <SiteCoreLinks />
    </main>
  );
}
